'use client';
import { isServer, QueryClient, QueryClientProvider as RQ_QueryClientProvider } from '@tanstack/react-query';

import { isStaleDeploymentError } from '@/lib/utils/stale_deployment';

const MAX_QUERY_RETRIES = 3;

function makeQueryClient() {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 60 * 1000,
        // Retrying a Server Action this deployment does not know can never succeed; only a
        // reload helps, so surface it at once instead of after the retry backoff.
        retry: (failureCount, error) => !isStaleDeploymentError(error) && failureCount < MAX_QUERY_RETRIES,
      },
    },
  });
}

let browserQueryClient: QueryClient | undefined = undefined;

function getQueryClient() {
  if (isServer) {
    // Server: always make a new query client
    return makeQueryClient();
  } else {
    // Browser: make a new query client if we don't already have one
    // This is very important, so we don't re-make a new client if React
    // suspends during the initial render. This may not be needed if we
    // have a suspense boundary BELOW the creation of the query client
    if (!browserQueryClient) browserQueryClient = makeQueryClient();
    return browserQueryClient;
  }
}

export function QueryClientProvider({ children }: { children: React.ReactNode }) {
  const queryClient = getQueryClient();

  return <RQ_QueryClientProvider client={queryClient}>{children}</RQ_QueryClientProvider>;
}
